import { Injectable } from '@angular/core';
import * as CryptoJS from 'crypto-js';

@Injectable({
  providedIn: 'root'
})
export class EncryptDecryptService {

  private readonly EC_PUBLIC_KEY_PEM = `-----BEGIN EC PUBLIC KEY-----
MFkwEwYHKoZIzj0CAQYIKoZIzj0DAQcDQgAElA8+zKAxYUfZajAWyhorbcfg7mxi
6cDbqQUB/B5nuyLQ3WomWIzS6BAN7djNf7YQGX1P+J6/BUzgedy7u9ViEQ==
-----END EC PUBLIC KEY-----`;

  // CHANGE: Added these two localStorage keys to persist the derived AES key and device public key.
  // Before this change, a brand-new ECDH key pair was generated on every page reload,
  // which produced a different AES key — making all tokens already saved in localStorage
  // unreadable (decryption would silently return garbage or throw).
  private readonly PERSISTED_DK_KEY = '_edk';   // stores the 16-char derived AES key
  private readonly PERSISTED_PUB_KEY = '_epk';  // stores the client's ECDH public key hex (Device-Key header)

  private key: CryptoJS.lib.WordArray = CryptoJS.lib.WordArray.create([]);
  private iv: CryptoJS.lib.WordArray = CryptoJS.lib.WordArray.create([]);

  deviceKeyHex: string = '';

  readonly ready: Promise<void>;

  constructor() {
    this.ready = this.initECDH().catch(err => {
      console.error('ECDH init failed:', err);
    });
  }

  // ─── ECDH key derivation ───────────────────────────────────────────────────

  private async initECDH(): Promise<void> {
    if (!window.crypto?.subtle) {
      throw new Error('Web Crypto API unavailable — app must be served over HTTPS for ECDH to work');
    }

    // CHANGE: On every page reload, check localStorage for a previously derived key first.
    // If found, restore it and skip the full ECDH flow entirely.
    // This ensures the same AES key is used session-to-session, so encrypted tokens
    // saved in localStorage on a previous load can still be decrypted correctly.
    // The key is only absent on the very first visit or after logout (clearPersistedKey).
    const storedDk  = localStorage.getItem(this.PERSISTED_DK_KEY);
    const storedPub = localStorage.getItem(this.PERSISTED_PUB_KEY);
    if (storedDk && storedPub) {
      this.deviceKeyHex = storedPub;
      this.key = CryptoJS.enc.Utf8.parse(storedDk);
      this.iv  = CryptoJS.enc.Utf8.parse(storedDk);
      return; // skip ECDH — key is already known
    }

    // 1. Import server's EC public key
    const serverPublicKey = await this.importECPublicKey(this.EC_PUBLIC_KEY_PEM);

    // 2. Generate client's ephemeral EC key pair (P-256, same curve as server)
    const clientKeyPair = await window.crypto.subtle.generateKey(
      { name: 'ECDH', namedCurve: 'P-256' },
      true,
      ['deriveBits']
    );

    const clientPubDer = await window.crypto.subtle.exportKey('spki', clientKeyPair.publicKey);
    this.deviceKeyHex = Array.from(new Uint8Array(clientPubDer))
      .map(b => b.toString(16).padStart(2, '0').toUpperCase())
      .join('');

    const sharedSecretBuf = await window.crypto.subtle.deriveBits(
      { name: 'ECDH', public: serverPublicKey },
      clientKeyPair.privateKey,
      256
    );

    // 5. Derive AES key — mirrors
    //    derivedKey = printHexBinary(sharedSecret).substring(0, 16)
    //    16 uppercase hex chars → CryptoJS.enc.Utf8.parse() → 16-byte AES-128 key
    const sharedSecretHex = Array.from(new Uint8Array(sharedSecretBuf))
      .map(b => b.toString(16).padStart(2, '0').toUpperCase())
      .join('');
    const derivedKey = sharedSecretHex.substring(0, 16);

    this.key = CryptoJS.enc.Utf8.parse(derivedKey);
    this.iv  = CryptoJS.enc.Utf8.parse(derivedKey);

    // CHANGE: After a fresh ECDH derivation, persist both values to localStorage.
    // On the next page load the block above will read these and skip ECDH entirely,
    // keeping the AES key stable across reloads.
    localStorage.setItem(this.PERSISTED_DK_KEY, derivedKey);
    localStorage.setItem(this.PERSISTED_PUB_KEY, this.deviceKeyHex);
  }

  // CHANGE: New method — called by StorageService.removeTokens() on logout.
  // Wipes the persisted key from localStorage so the next login generates a fresh
  // ECDH pair and a new AES key, preventing the old key from being reused after logout.
  clearPersistedKey(): void {
    localStorage.removeItem(this.PERSISTED_DK_KEY);
    localStorage.removeItem(this.PERSISTED_PUB_KEY);
  }

  private async importECPublicKey(pem: string): Promise<CryptoKey> {
    const pemContents = pem
      .replace('-----BEGIN EC PUBLIC KEY-----', '')
      .replace('-----END EC PUBLIC KEY-----', '')
      .replace(/\s/g, '');

    const binaryDer = Uint8Array.from(atob(pemContents), c => c.charCodeAt(0));

    return window.crypto.subtle.importKey(
      'spki',
      binaryDer.buffer,
      { name: 'ECDH', namedCurve: 'P-256' },
      false,
      [] // no key usages needed for a public key in ECDH
    );
  }


  encryptData(value: any): string {
    if (value === undefined || value === null) return '';
    const encrypted = CryptoJS.AES.encrypt(
      CryptoJS.enc.Utf8.parse(value.toString()),
      this.key,
      { keySize: 128 / 8, iv: this.iv, mode: CryptoJS.mode.CBC, padding: CryptoJS.pad.Pkcs7 }
    );
    return encrypted.toString();
  }

  decryptData(value: string): string {
    const decrypted = CryptoJS.AES.decrypt(value, this.key, {
      keySize: 128 / 8,
      iv: this.iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7
    });
    return decrypted.toString(CryptoJS.enc.Utf8);
  }
}
